export const MirthDocumentationMarkdown = `
# Send an outbound message through the interface engine

This block sends an outbound payload to your organization's interface engine listener.

---

#### **IMPORTANT NOTE:** Currently, there is no validation (*coming soon*) to ensure the HL7/X12 messages are correct and properly formatted, so it is up to you to make sure. For now, the *HL7/X12 Messages Overview* section below should serve as a useful guide to get you started.

---

## Configuration Options

### Message Type
This is the type of message to send. It will only show options applicable to the current Notable interface engine configuration. If using HL7, choose "HL7" and if using X12, choose "X12". If sending a custom/unlisted message/payload type, choose "JSON".

### Interface Engine Path
This is the path to the interface engine to send the message to. This is an optional configuration. If not provided, the message will be sent directly to the PORT of the interface engine listener for your organization without a PATH, and the interface engine will route the message for you.

### Message
This is the message to send.


### Timeout
This is the maximum duration the block will wait for a response from the Interface Engine before retrying. The default is 45 seconds and the maximum is 20 minutes. A longer timeout may be desired when sending larger files or for high volume implementations that may encounter queueing in the Interface Engine.



## Useful Links
The external HL7 pages below are for version 2.2, so please update it to whichever version you are working with as there could be slight variations to how the message is constructed between different versions.
- [HL7 Message Overview](https://hl7-definition.caristix.com/v2)
- [Trigger Events](https://hl7-definition.caristix.com/v2/HL7v2.2/TriggerEvents)
- [ADT A08 Message Type](https://hl7-definition.caristix.com/v2/HL7v2.2/TriggerEvents/ADT_A08)
- [MSH Fields](https://hl7-definition.caristix.com/v2/HL7v2.2/Segments/MSH)
- [Message Type Field Definition](https://hl7-definition.caristix.com/v2/HL7v2.2/DataTypes/CM_MSG)
- [HL7 Values Tables](https://hl7-definition.caristix.com/v2/HL7v2.2/Tables)
- [X12 Transaction Sets Overview](https://x12.org/products/transaction-sets)
- [278 Companion Guide](https://www.cms.gov/files/document/esmd-x12n-278-companion-guide.pdf)
- [Common X12 Examples](https://x12.org/examples)

## HL7 Message Overview

HL7 messages are defined by **trigger events** and are mainly composed of **segments** and **fields**:

#### Trigger Events
Trigger events represent the type of message being sent. Messages are sent due to the need for data to flow between systems in response to real world events in healthcare. For example, the trigger event "ADT A08" is used to update patient information that has
changed in the EHR.

A breakdown of different trigger events and their standard segments and fields can be found [here](https://hl7-definition.caristix.com/v2/HL7v2.2/TriggerEvents).

#### Segments
Segments represent information about different parts of the message such as the message itself (MSH), a patient (PID), or an order (ORC) on a single line. They always start with a unique, three-character identifier (e.g. MSH) and have standardized structures
composed of fields. The first segment of any message is always the MSH segment.

A breakdown of segments that can be used in an ADT A08 trigger event can be found [here](https://hl7-definition.caristix.com/v2/HL7v2.2/TriggerEvents/ADT_A08).

#### Fields
Fields define the different parts of a segment. They are separated by the | (pipe) symbol and have standardized formats and data types. Not all fields are required, but empty fields need to be included with the separator (||).

A breakdown of fields in the MSH segment can be found [here](https://hl7-definition.caristix.com/v2/HL7v2.2/Segments/MSH)

#### Sample HL7 Message

\`\`\`
MSH|^~\\&|NOTABLE|NOTABLE|RECEIVING_APP|RECEIVING_FACILITY|20250101010100||ADT^A08|MSG00001|T|2.3
PID|1||12345^^^MRN||DOE^JOHN^^^^||19800101|M|||
\`\`\`

## X12 Message Overview

X12 messages are standardized EDI (Electronic Data Interchange) formats widely used in healthcare for billing, claims, and other administrative transactions. Each message is composed of **segments**, **elements**, and **delimiters**:

#### Segments
Segments are lines of data that start with a unique identifier. Common segments include:
- ISA: Interchange Control Header (always the first segment)
- GS: Functional Group Header
- ST: Transaction Set Header
- BHT: Beginning of Hierarchical Transaction
- HL: Hierarchical Level
- NM1: Individual or Organizational Name
- SE: Transaction Set Trailer
- GE: Functional Group Trailer
- IEA: Interchange Control Trailer

#### Elements
Elements are individual data fields within segments, separated by an asterisk (*). Each element has a specific meaning based on its position and the segment type. Elements can be:
- Required or Optional
- Have specific length requirements
- Have specific data type requirements (numeric, alpha, alphanumeric)

#### Delimiters
X12 uses standard delimiters:
- *: Element separator
- ~: Segment terminator
- ^: Sub-element separator
- \\: Repetition separator

#### Sample X12 Message

\`\`\`
ISA00 00 ZZNOTABLEENG ZZCNE 2303261200^005010000009050T:~
GSHNNOTABLEENGCNE202503261200905X005010X278~
ST2780001005010X278~
BHT000713REF123456202503261200~
HL1201~
NM1P32Referral ProviderXX1234567890~
HL21211~
NM11P1JohnDoeXX9876543210~
SE90001~
GE1905~
IEA1000000905~
\`\`\`

## Limitations
#### Field validation
The data in fields usually require specific data types, formats, and/or lengths, but there might not be validations done at the time of sending.

For example, the message type field in the MSH segment expects the [Message Type](https://hl7-definition.caristix.com/v2/HL7v2.2/DataTypes/CM_MSG) data type, but does not have validations to check that it is correctly formatted. In this case, we need to
refer to the HL7 documentation to ensure correct formatting. Additionally, it is important to check the documentation for the available coded values for the specific field.

For this message type field, the available values for the first part of the data can be found [here](https://hl7-definition.caristix.com/v2/HL7v2.2/Tables/0076) and for the second part [here](https://hl7-definition.caristix.com/v2/HL7v2.2/Tables/0003) (e.g. "ADT^A08").

This limitation around validation of correct field formatting will be solved in the future when we introduce a better and easier way to construct messages, which will include live validation while you're building the message.

## Best practices
While constructing the HL7/X12 messages: 
- refer to the various external HL7/X12 resources and documentation linked above to ensure each field and segment in the message is correct.
- try to use outputs from previous blocks as much as possible to prevent having to create multiple variations of the same flow to cater to different parameters.
- use script blocks if needed to transform various data into what is expected for each field.
- make sure to press enter at the end of each segment to start a new segment, as segments are single-line and need to be separated by line breaks.
`;
